import {
  alpha,
  createTheme,
  lighten,
  type Theme,
  type ThemeOptions,
} from "@mui/material/styles";
import {
  RAVEN_FONT_STACKS,
  ravenAdminCopperDarkTheme,
  ravenAdminCopperLightTheme,
  type RavenFontFamily,
} from "@ravenappdev/raven-ui";
import CancelRoundedIcon from "@mui/icons-material/CancelRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import { createElement } from "react";

// ESS runs the design-system copper admin theme
// (`createRavenAdminTheme({ primaryHex: "#B35300", brand: { main: "#ED6C02",
// dark: "#B35300", navActive: "#954600" } })`) and layers only what is
// genuinely ESS on top. Everything brand-tied — the three copper tiers, the
// `--cta-*` contained-button recipe, the `--tf-*` text-field recipe, the
// pill radius, the type scale, the `tableHeader` variant, the shadow ramp —
// is the package's. Read tokens from `theme.palette.*`; do not reintroduce
// brand hex here or in components.
//
// The warm neutrals below are the exception: ESS sits on parchment, not
// white, and the design system's locked neutrals (`rgba(0,0,0,0.87)` text,
// `#FFFFFF` page) are a different surface. They live here as named
// constants so the palette and the component overrides can't drift apart.
//
// They are LIGHT-MODE ONLY. Dark mode takes the package's own dark neutrals
// (near-black page, lifted paper, teal-leaning text) rather than a hand-rolled
// warm dark ramp: the package's set is the locked, mode-tested one and it
// carries the WCAG gate. Anything the package does not ship a dark token for
// is derived from one via `alpha` / `lighten` — never a new literal, so the
// hex gate stays at 0. See `essNeutrals` below.
export type EssColorMode = "light" | "dark";

const ESS_PAGE_BG = "#FAF7F2";
const ESS_DIVIDER = "#E9E5DF";
const ESS_NAV_ITEM_BG = "#FDECE0";
const ESS_TEXT_PRIMARY = "#0E0D10";
const ESS_TEXT_SECONDARY = "#6B6875";

// Raven's card signature in light mode: hairline warm border plus a soft,
// wide, copper-tinted spread. Radius comes from the package (24).
const ESS_CARD_SHADOW =
  "0 1px 0 rgba(14,13,16,0.04), 0 30px 60px -30px rgba(14,13,16,0.18), 0 14px 30px -18px rgba(194,65,12,0.18)";

const DARK = ravenAdminCopperDarkTheme.palette;

/**
 * The neutrals the component overrides paint with, resolved per mode.
 *
 * Values are read off the package's resolved palettes at module load rather
 * than through an `sx` callback on purpose: `createTheme(base, overrides)`
 * deep-merges plain objects but *replaces* a slot the package defines as a
 * callback (and vice versa), so every slot below stays a plain object.
 */
function essNeutrals(mode: EssColorMode) {
  if (mode === "light") {
    return {
      divider: ESS_DIVIDER,
      textPrimary: ESS_TEXT_PRIMARY,
      textSecondary: ESS_TEXT_SECONDARY,
      cardBorder: ESS_DIVIDER,
      cardShadow: ESS_CARD_SHADOW,
      // Near-black glass. MUI's own tooltip colour (white) is left to the
      // default so the light theme emits exactly the rules it emits today.
      tooltipBg: "rgba(14,13,16,0.92)",
      tooltipColor: undefined as string | undefined,
    };
  }
  // Dark mode, per the design guide's dark rules: lifted surfaces carry
  // separation, borders do the work shadows do in light, and no elevation
  // overlay. Every value derives from a package dark token.
  return {
    divider: String(DARK.divider),
    textPrimary: DARK.text.primary,
    textSecondary: DARK.text.secondary,
    cardBorder: String(DARK.divider),
    // A 30px copper-tinted spread is invisible on a near-black page, and the
    // faint one that does register reads as grime. The hairline border above
    // is the separation; the shadow collapses to a single contact layer.
    cardShadow: `0 1px 2px ${alpha(DARK.background.default, 0.6)}`,
    // Tooltips and menus lift ABOVE the page in dark mode — a near-black
    // tooltip on a near-black page has no edge at all.
    tooltipBg: alpha(lighten(DARK.background.paper, 0.22), 0.96),
    tooltipColor: DARK.text.primary as string | undefined,
  };
}

const ALERT_SEVERITIES = ["success", "info", "warning", "error"] as const;

/** Dark-mode `standard*` Alert slots, derived from the package's dark
 * severity colours. Plain objects, so they deep-merge over the package's. */
export function darkStandardAlerts() {
  const slot = (key: (typeof ALERT_SEVERITIES)[number]) => {
    const main = DARK[key].main;
    return {
      backgroundColor: alpha(main, 0.14),
      color: lighten(main, 0.7),
      "& .MuiAlert-icon": { color: main },
    };
  };
  return {
    standardSuccess: slot("success"),
    standardInfo: slot("info"),
    standardWarning: slot("warning"),
    standardError: slot("error"),
  };
}

function essThemeOverrides(mode: EssColorMode): ThemeOptions {
  const n = essNeutrals(mode);
  const isLight = mode === "light";

  return {
    palette: isLight
      ? {
        // `dark` is the design system's page-background token (the app rail
        // reads it); ESS's page is the same parchment as `default`, not the
        // near-white the package derives from the copper ramp.
        background: { default: ESS_PAGE_BG, dark: ESS_PAGE_BG },
        text: { primary: ESS_TEXT_PRIMARY, secondary: ESS_TEXT_SECONDARY },
        // Nothing consumes `secondary` today. It stays on ESS's neutral dark
        // rather than inheriting the package's accent hue, which for the
        // hex-anchored copper theme resolves to deepPurple — the first
        // `color="secondary"` anyone writes would come out purple.
        secondary: {
          main: "#2A2830",
          light: "#4A4750",
          dark: "#0E0D10",
          contrastText: "#FFFFFF",
        },
        success: { main: "#2F7D4E" },
        error: { main: "#D32F2F" },
        // warning.main is a second ESS accent (SummaryCard tiles, Autocomplete
        // option wash), so it stays on the deeper ESS orange rather than the
        // design system's #FF9800.
        warning: {
          main: "#E65100",
          light: "#F57C00",
          dark: "#BF360C",
          contrastText: "#FFFFFF",
        },
        divider: ESS_DIVIDER,
        navItemBg: ESS_NAV_ITEM_BG,
      }
      : // Dark mode overrides NOTHING in the palette. Every slot ESS pins in
      // light — page, text, divider, navItemBg, the status set — has a
      // package dark counterpart that is already lifted for a near-black
      // canvas and already through the factory's dark WCAG gate. ESS's
      // light values would all fail there: #FAF7F2 is a white page,
      // #0E0D10 text is invisible, #2F7D4E drops under 4.5:1. The one
      // deliberate difference from light is that the package keeps the rail
      // (`background.dark`) a step lifted from the page
      // (`background.default`) instead of flat — which is the dark-mode
      // surface hierarchy the guide asks for.
      {},
    // 34 `sx={{ borderRadius: n }}` callsites multiply this, so it stays at 10
    // until those are swept; the package's own surfaces set radii explicitly.
    shape: { borderRadius: 10 },
    components: {
      MuiButton: {
        styleOverrides: {
          // The design system tokenises button-medium (14px) and button-large
          // (15px) but not small, so `size="small"` would fall back to MUI's
          // 13px. ESS runs every button at the medium step.
          root: { fontSize: "0.875rem" },
          outlinedPrimary: ({ theme }) => ({
            color: theme.palette.primary.main,
            // Border at 50% alpha for a softer rim against tinted surfaces.
            // Hover keeps the same alpha — signal lives in the bg wash, not
            // in border darkening.
            borderColor: alpha(theme.palette.primary.main, 0.5),
            transition:
              "transform .08s ease, background-color .15s ease, border-color .15s ease",
            "&:hover": {
              backgroundColor: alpha(theme.palette.primary.main, 0.06),
              borderColor: alpha(theme.palette.primary.main, 0.5),
            },
            "&:active": {
              transform: "translateY(1px)",
              backgroundColor: alpha(theme.palette.primary.main, 0.1),
            },
            "&.Mui-disabled": {
              opacity: 0.55,
              color: theme.palette.primary.main,
              borderColor: alpha(theme.palette.primary.main, 0.5),
            },
          }),
        },
      },
      MuiTab: {
        styleOverrides: {
          // ESS marks the selected tab with the indicator + near-black label
          // rather than a copper label, so a tab strip doesn't compete with
          // the CTAs beside it.
          root: {
            color: n.textSecondary,
            "&.Mui-selected": { color: n.textPrimary },
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            border: `1px solid ${n.cardBorder}`,
            boxShadow: n.cardShadow,
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          // The package rounds the drawer paper's leading corners for a
          // right-anchored side sheet. ESS's rail is anchored left, where the
          // same radius notches the screen edge.
          paperAnchorLeft: { borderTopLeftRadius: 0, borderBottomLeftRadius: 0 },
        },
      },
      MuiPaper: {
        styleOverrides: {
          // The package rounds every Paper root, which also rounds the
          // `square` ones — the fixed AppBar picks up notched top corners.
          // Restore MUI's square default and keep ESS's 14px rounded step.
          root: { borderRadius: 0 },
          rounded: { borderRadius: 14 },
        },
      },
      // disableScrollLock across every overlay: MUI's default scroll-lock
      // sets `body { overflow: hidden }` when an overlay opens, which removes
      // the ~14px scrollbar and grows the layout viewport — re-evaluating the
      // responsive breakpoints and reflowing the page under the cursor. With
      // the scrollbar permanently reserved (index.css `html {overflow-y:scroll}`)
      // and the lock disabled, the viewport width is invariant to any overlay.
      // Dialog / Menu / Popover each forward their own prop to Modal, so all
      // four slots need it. Mode-independent.
      MuiModal: {
        defaultProps: { disableScrollLock: true },
      },
      MuiDialog: {
        defaultProps: { disableScrollLock: true },
        styleOverrides: {
          // MUI renders dialogs at elevation 24; ESS softens to the 8 step.
          // In dark mode a spread shadow cannot separate paper from page, so
          // the dialog also takes the hairline the cards take.
          paper: isLight
            ? { boxShadow: ravenAdminCopperLightTheme.shadows[8] }
            : {
              boxShadow: ravenAdminCopperDarkTheme.shadows[8],
              border: `1px solid ${n.divider}`,
            },
        },
      },
      MuiMenu: {
        defaultProps: { disableScrollLock: true },
      },
      MuiPopover: {
        defaultProps: { disableScrollLock: true },
      },
      MuiTableCell: {
        // ESS tables are denser than the design system default.
        styleOverrides: {
          root: { padding: "10px 16px" },
          sizeSmall: { padding: "8px 12px" },
          head: { padding: "10px 12px", lineHeight: 1.4 },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: n.tooltipBg,
            ...(n.tooltipColor ? { color: n.tooltipColor } : {}),
            fontSize: "13.5px",
            lineHeight: 1.5,
            borderRadius: 6,
            letterSpacing: "0.2px",
            padding: "8px 12px",
          },
        },
      },
      MuiTablePagination: {
        styleOverrides: {
          // The pagination toolbar packs label + select + range + 2 nav
          // chevrons on a single line. At mobile widths (≤600px) it
          // overflows and the trailing chevron clips. Tighten paddings,
          // hide the "Rows per page:" label (the select value still
          // communicates page size), and remove the flex spacer so the
          // remaining controls fit on one row.
          toolbar: {
            paddingLeft: 8,
            "@media (max-width: 600px)": {
              paddingRight: 0,
              minHeight: 48,
              "& .MuiTablePagination-spacer": { display: "none" },
              "& .MuiTablePagination-selectLabel": { display: "none" },
              "& .MuiTablePagination-input": { marginRight: 12 },
              "& .MuiTablePagination-displayedRows": { marginLeft: "auto" },
              "& .MuiTablePagination-actions": { marginLeft: 8 },
            },
          },
        },
      },
      // Glyph slots the package theme does not wire to Lucide yet (it covers
      // Select / NativeSelect / Checkbox / TablePagination). Until it does,
      // these keep the icons ESS already ships rather than falling back to
      // MUI's filled defaults.
      MuiAutocomplete: {
        defaultProps: {
          popupIcon: createElement(ExpandMoreRoundedIcon),
          clearIcon: createElement(CloseRoundedIcon, { fontSize: "small" }),
        },
      },
      MuiChip: {
        defaultProps: {
          deleteIcon: createElement(CancelRoundedIcon),
        },
      },
      MuiAlert: {
        defaultProps: {
          iconMapping: {
            success: createElement(CheckRoundedIcon, { fontSize: "inherit" }),
          },
        },
        // The package pins every standard Alert to its light pastel bg in
        // both modes, while MUI's dark text is a near-white lighten of the
        // same pastel — ~1.05:1. Dark mode re-derives all three from the
        // lifted `main`: a 14% wash, a light tint for text, `main` for the
        // icon (AA pinned in appearance.test.tsx).
        ...(isLight ? {} : { styleOverrides: darkStandardAlerts() }),
      },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            // MUI marks keyboard focus with `action.focus` alone, a wash too
            // faint to find on the nav rail or in a menu. A ring in the
            // utility copper (light, AA on parchment) / lifted brand (dark).
            "&.Mui-focusVisible": {
              outline: `2px solid ${
                isLight
                  ? (ravenAdminCopperLightTheme.palette.navActive ??
                    ravenAdminCopperLightTheme.palette.primary.dark)
                  : DARK.primary.main
              }`,
              outlineOffset: -2,
            },
            // The package's `MuiListItemButton` hover reads
            // `palette.purple[100]`, which is the copper ramp's 50 step in
            // BOTH modes — a near-white blob on a dark rail. Retarget it to
            // the brand wash, which is mode-aware. Upstream candidate; filed
            // in the CHANGELOG.
            ...(isLight
              ? {}
              : {
                "&:hover": {
                  backgroundColor: alpha(DARK.primary.main, 0.08),
                },
              }),
          },
        },
      },
    },
  };
}

/**
 * The ESS theme for a colour mode, optionally with the user's font choice.
 *
 * `light` is the theme ESS has always shipped — `theme` below is the same
 * object, kept as a named export for the test suites that import it.
 * `dark` layers the same ESS structure on the package's dark copper palette.
 */
export function buildEssTheme(
  mode: EssColorMode,
  font: RavenFontFamily = "source-sans-3",
): Theme {
  const base =
    mode === "light" ? ravenAdminCopperLightTheme : ravenAdminCopperDarkTheme;
  const overrides = essThemeOverrides(mode);
  // The font preference lives on the package's own `typography.fontFamily`
  // (and the `tableHeader` variant). Only re-declare it when the user has
  // actually moved off the brand font, so the default build stays identical
  // to `createTheme(base, overrides)`.
  if (font === "source-sans-3") return createTheme(base, overrides);
  const stack = RAVEN_FONT_STACKS[font];
  return createTheme(base, overrides, {
    typography: {
      fontFamily: stack,
      tableHeader: { fontFamily: stack },
    },
  });
}

export const theme = buildEssTheme("light");
